import React, { useEffect, useState } from 'react';

import { getExceptionReport } from '../../api_handlers/invoice';
import { Alert, Empty, ICONS, Modal, money, relative } from './kit';

/**
 * Exception usage report (IN-1718, Flow 2).
 *
 * The exception link is meant to stay an exception. This answers who is
 * using it, on which campaigns, and who on staff is handing links out, over
 * the same campaign / type / date filters as the console.
 */

export const REPORT_CSS = `
.ivr-totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 22px; }
.ivr-total { border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 12px 14px; }
.ivr-total__label { font-size: 10.5px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 5px; }
.ivr-total__value { font-size: 18px; font-weight: 800; color: var(--ink); }
.ivr-total__value--warn { color: #B45309; }
.ivr-total__sub { font-size: 11.5px; color: var(--ink-4); margin-top: 3px; }
.ivr-section { margin-bottom: 24px; }
.ivr-section__title { font-size: 13px; font-weight: 800; color: var(--ink); margin: 0 0 8px; }
.ivr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ivr-table th { text-align: left; font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); padding: 7px 8px; border-bottom: 1px solid var(--line); white-space: nowrap; }
.ivr-table td { padding: 9px 8px; border-bottom: 1px solid #F1F3F7; color: var(--ink-2); vertical-align: top; }
.ivr-table tr:last-child td { border-bottom: 0; }
.ivr-table .ivr-r { text-align: right; }
.ivr-name { font-weight: 700; color: var(--ink); }
.ivr-sub { font-size: 11.5px; color: var(--ink-4); }
.ivr-flag { display: inline-block; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #B91C1C; background: #FEE2E2; border-radius: 999px; padding: 1px 7px; }
.ivr-link { border: 0; background: none; padding: 0; font: inherit; color: var(--accent-ink); font-weight: 700; cursor: pointer; }
.ivr-link:hover { text-decoration: underline; }
@media (max-width: 700px) { .ivr-totals { grid-template-columns: repeat(2, 1fr); } }
`;

function percent(share) {
  return `${Math.round((share || 0) * 100)}%`;
}

function Total({ label, value, sub, warn }) {
  return (
    <div className="ivr-total">
      <p className="ivr-total__label">{label}</p>
      <div className={`ivr-total__value iv-num${warn ? ' ivr-total__value--warn' : ''}`}>
        {value}
      </div>
      {sub ? <div className="ivr-total__sub">{sub}</div> : null}
    </div>
  );
}

export default function ExceptionReport({
  open,
  filters,
  onClose,
  onShowUnmatched,
  onShowCampaign,
}) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const campaignId = filters.campaign_id;
  const campaignSubtype = filters.campaign_subtype;
  const after = filters.submitted_after;
  const before = filters.submitted_before;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setReport(null);
    setError('');
    getExceptionReport({
      campaign_id: campaignId,
      campaign_subtype: campaignSubtype,
      submitted_after: after,
      submitted_before: before,
    })
      .then((data) => {
        if (live) setReport(data);
      })
      .catch((e) => {
        if (live) setError(e.message || 'Could not load the report.');
      });
    return () => {
      live = false;
    };
  }, [open, campaignId, campaignSubtype, after, before]);

  const totals = (report && report.totals) || {};
  const scoped = campaignId || campaignSubtype || after || before;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exception usage"
      subtitle={
        scoped
          ? 'Invoices that came in through an exception link, for the current filters.'
          : 'Invoices that came in through an exception link, across all campaigns.'
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      {!report && !error ? (
        <div className="iv-sk" style={{ height: 220, borderRadius: 12 }} />
      ) : null}
      {report ? (
        <>
          <div className="ivr-totals">
            <Total
              label="Exception invoices"
              value={totals.invoices || 0}
              sub={`${percent(totals.share)} of ${totals.all_invoices || 0}`}
              warn={(totals.share || 0) > 0.2}
            />
            <Total label="Amount" value={money(totals.amount)} />
            <Total
              label="Unverified"
              value={totals.unmatched || 0}
              sub="submitter not in master data"
              warn={(totals.unmatched || 0) > 0}
            />
            <Total
              label="Links issued"
              value={totals.links_issued || 0}
              sub={`${totals.links_used || 0} used · ${totals.links_active || 0} active`}
            />
          </div>

          {totals.unmatched ? (
            <p className="iv-hint" style={{ margin: '-10px 0 20px' }}>
              <button type="button" className="ivr-link" onClick={onShowUnmatched}>
                Show the {totals.unmatched} unverified invoice{totals.unmatched === 1 ? '' : 's'}
              </button>{' '}
              — verify who sent them before they are paid.
            </p>
          ) : null}

          {(report.by_submitter || []).length === 0 ? (
            <Empty icon={ICONS.check} title="No exception invoices">
              Nothing has come in through an exception link for these filters.
            </Empty>
          ) : (
            <>
              <div className="ivr-section">
                <p className="ivr-section__title">By who submitted</p>
                <table className="ivr-table">
                  <thead>
                    <tr>
                      <th>Submitter</th>
                      <th>Type</th>
                      <th className="ivr-r">Invoices</th>
                      <th className="ivr-r">Campaigns</th>
                      <th className="ivr-r">Amount</th>
                      <th>Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_submitter.map((row) => (
                      <tr key={`${row.submitter_type}-${row.email || row.agency || row.name}`}>
                        <td>
                          <div className="ivr-name">{row.agency || row.name || 'Not recorded'}</div>
                          <div className="ivr-sub">
                            {[row.agency ? row.name : '', row.email].filter(Boolean).join(' · ')}
                          </div>
                          {row.unmatched ? (
                            <span className="ivr-flag">{row.unmatched} unverified</span>
                          ) : null}
                        </td>
                        <td>{row.label}</td>
                        <td className="ivr-r iv-num">{row.invoices}</td>
                        <td className="ivr-r iv-num">{row.campaigns}</td>
                        <td className="ivr-r iv-num">{money(row.amount)}</td>
                        <td className="ivr-sub">{relative(row.last_submitted_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ivr-section">
                <p className="ivr-section__title">By campaign</p>
                <table className="ivr-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th className="ivr-r">Exception</th>
                      <th className="ivr-r">Share</th>
                      <th className="ivr-r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.by_campaign || []).map((row) => (
                      <tr key={row.campaign_id}>
                        <td>
                          <button
                            type="button"
                            className="ivr-link"
                            onClick={() => onShowCampaign(row.campaign_id)}
                          >
                            {row.campaign_name}
                          </button>
                          <div className="ivr-sub">
                            {row.brand_name ? `${row.brand_name} · ` : ''}#{row.campaign_id}
                            {row.unmatched ? ` · ${row.unmatched} unverified` : ''}
                          </div>
                        </td>
                        <td className="ivr-r iv-num">
                          {row.invoices} of {row.all_invoices}
                        </td>
                        <td className="ivr-r iv-num">{percent(row.share)}</td>
                        <td className="ivr-r iv-num">{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ivr-section" style={{ marginBottom: 0 }}>
                <p className="ivr-section__title">By who issued the link</p>
                <table className="ivr-table">
                  <thead>
                    <tr>
                      <th>Staff member</th>
                      <th className="ivr-r">Invoices</th>
                      <th className="ivr-r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.by_issuer || []).map((row) => (
                      <tr key={row.user_id || 'unknown'}>
                        <td className="ivr-name">{row.name}</td>
                        <td className="ivr-r iv-num">{row.invoices}</td>
                        <td className="ivr-r iv-num">{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : null}
    </Modal>
  );
}
